const API_URL = import.meta.env.VITE_API_URL || '';

export interface EscrowedShellPassword {
    nodeId: string;
    password: string;
    version: number;
    escrowedAt: string;
}

export type ShellPasswordOutcome =
    | { kind: 'revealed'; value: EscrowedShellPassword }
    | { kind: 'none' }
    | { kind: 'forbidden' }
    | { kind: 'unreadable' }
    | { kind: 'error'; status: number };

/**
 * What a shell-password lookup meant.
 *
 * The route returns four outcomes that a technician must act on differently, and the one that
 * matters most is the pair that look alike. 404 means this node has no escrowed password, so the
 * technician moves on. 500 means a password IS escrowed and the server could not decrypt it -
 * a rotated or lost DataProtection key ring. NodeShellPasswordsController says why it is
 * deliberately not a 404: "a 404 here would send the technician hunting for a typo in the node id
 * at the one moment they need the password, while a 500 tells them to escalate."
 *
 * Collapsing them into one "not found" would throw away the distinction the server went out of
 * its way to make, so they are separate outcomes here and carry separate messages.
 */
export const readShellPasswordResponse = async (
    response: { status: number; json: () => Promise<unknown> },
): Promise<ShellPasswordOutcome> => {
    switch (response.status) {
        case 200:
            return { kind: 'revealed', value: (await response.json()) as EscrowedShellPassword };
        case 403:
            // The route deliberately does not reveal whether a password exists for this node, so
            // neither does this. Every refusal is audited server-side either way.
            return { kind: 'forbidden' };
        case 404:
            return { kind: 'none' };
        case 500:
            return { kind: 'unreadable' };
        default:
            return { kind: 'error', status: response.status };
    }
};

export const describeShellPasswordOutcome = (outcome: ShellPasswordOutcome): string => {
    switch (outcome.kind) {
        case 'revealed':
            return '';
        case 'none':
            return 'No password is escrowed for this node. Nothing has been lost: a node files its '
                + 'password on first boot, so a node that has never completed one has nothing here.';
        case 'forbidden':
            return 'You are not authorized to read node shell passwords. This attempt has been recorded.';
        case 'unreadable':
            return 'A password IS escrowed for this node, but the server could not decrypt it. '
                + 'This is a server fault, not a wrong node id. Escalate rather than retrying.';
        case 'error':
            return `The lookup failed with HTTP ${outcome.status}.`;
    }
};

export const fetchShellPassword = async (nodeId: string): Promise<ShellPasswordOutcome> => {
    const response = await fetch(`${API_URL}/api/admin/nodes/${nodeId}/shell-password`, {
        credentials: 'include',
    });

    return readShellPasswordResponse(response);
};
